import classNames from "classnames";
import React, {FC, memo} from "react";
import {Tag} from "antd";
import NotFound from "../../containers/common/NotFound";
import {GetBookingByIdQuery} from "../../generated/graphql";
import i18n from "../../services/i18n";
import PanelCard from "../PanelCard/PanelCard";
import css from "./PanelCardBooker.less";

type PanelCardBookerProps = {
    attendee: GetBookingByIdQuery["bookingById"]["attendee"];
    className?: string;
};

const PanelCardBooker: FC<PanelCardBookerProps> = ({attendee, className}) => {
    type ContactCardCSSProperties = React.CSSProperties & {
        "--column-count": number;
    };

    const actualStyle: ContactCardCSSProperties = {
        "--column-count": 1,
        // ...style,
    };

    if (attendee?.booker === undefined) {
        return <NotFound />;
    }

    const {booker} = attendee;

    /** Ermittelt die Rechnungsadresse und deren Quelle (Attendee oder Booker) */
    const getBillingAddressInfo = (): {source: "attendee" | "booker"; companyName?: string | null; firstname?: string | null; lastname?: string | null; addition?: string | null; street?: string | null; postCode?: string | null; city?: string | null} | null => {
        if (attendee.hasBillingAddress) {
            return {
                source: "attendee",
                companyName: attendee.billingCompanyName,
                firstname: attendee.billingFirstname,
                lastname: attendee.billingLastname,
                addition: attendee.billingAddition,
                street: attendee.billingStreet,
                postCode: attendee.billingPostCode,
                city: attendee.billingCity,
            };
        }
        if (booker?.hasBillingAddress) {
            return {
                source: "booker",
                companyName: booker.billingCompanyName,
                firstname: booker.billingFirstname,
                lastname: booker.billingLastname,
                addition: booker.billingAddition,
                street: booker.billingStreet,
                postCode: booker.billingPostCode,
                city: booker.billingCity,
            };
        }
        return null;
    };

    const billingInfo = getBillingAddressInfo();

    return (
        <PanelCard columns={3} title={i18n.containers.invoice.InvoiceDetails.attendeeInfo()} gapAfter className={className} autoHeight={true}>
            <div className={classNames(css.withDivider, css.container)} style={actualStyle}>
                <div className={css.label}>{i18n.containers.invoice.InvoiceDetails.attendee()}</div>
                <div className={css.entry}>{`${attendee.firstname} ${attendee.lastname}`}</div>
                <div className={css.label}>{i18n.containers.invoice.InvoiceDetails.booker()}</div>
                <div className={css.entry}>{`${booker?.firstname} ${booker?.lastname}`}</div>
            </div>
            <div className={classNames(css.withDivider, css.container)} style={actualStyle}>
                <div className={css.label}>{i18n.containers.invoice.InvoiceDetails.address()}</div>
                <div className={css.entry}>
                    {`${booker?.street} ${booker?.houseNumber}`}
                    <br />
                    {`${booker?.postCode} ${booker?.city}`}
                </div>
                {billingInfo && (
                    <React.Fragment>
                        <div className={css.label}>
                            {i18n.containers.invoice.InvoiceDetails.billingAddress()}{" "}
                            <Tag style={{marginLeft: 4}}>
                                {billingInfo.source === "attendee"
                                    ? i18n.containers.invoice.InvoiceDetails.billingSourceAttendee()
                                    : i18n.containers.invoice.InvoiceDetails.billingSourceBooker()}
                            </Tag>
                        </div>
                        <div className={css.entry}>
                            {billingInfo.companyName && (
                                <React.Fragment>
                                    {billingInfo.companyName}
                                    <br />
                                </React.Fragment>
                            )}
                            {`${billingInfo.firstname || ""} ${billingInfo.lastname || ""}`.trim()}
                            {billingInfo.addition && (
                                <React.Fragment>
                                    <br />
                                    {billingInfo.addition}
                                </React.Fragment>
                            )}
                            <br />
                            {billingInfo.street || ""}
                            <br />
                            {`${billingInfo.postCode || ""} ${billingInfo.city || ""}`.trim()}
                        </div>
                    </React.Fragment>
                )}
            </div>
            <div className={classNames(css.container)} style={actualStyle}>
                <div className={css.label}>{i18n.containers.invoice.InvoiceDetails.contactData.label()}</div>
                {/* Leere Felder nicht als „Mobil null“ zeigen (Befund 14, 30.10.2026) */}
                {booker?.phoneNumber && <div className={css.entry}>{`${i18n.containers.invoice.InvoiceDetails.contactData.phone()} ${booker.phoneNumber}`}</div>}
                {booker?.mobileNumber && <div className={css.entry}>{`${i18n.containers.invoice.InvoiceDetails.contactData.mobile()} ${booker.mobileNumber}`}</div>}
                {booker?.email && <div className={css.entry}>{`${i18n.containers.invoice.InvoiceDetails.contactData.email()} ${booker.email}`}</div>}
            </div>
        </PanelCard>
    );
};

export default memo(PanelCardBooker);
